/**
 * MilkdownEditor — the ProseMirror/Milkdown writing surface
 * (spec.md § Editor, § Visual design system / Markdown rendering).
 *
 * Wiring:
 * - GFM preset (`@milkdown/preset-gfm`) → headings, bold/italic/strikethrough,
 *   ordered + unordered lists, GFM tables, task lists (`- [ ]`), inline + fenced
 *   code, blockquotes, links, and images by URL (`![alt](url)` embeds).
 * - `listener` plugin → reports the serialized markdown on every change via
 *   `markdownUpdated`, which we lift to the save machine as the live body.
 * - The editor stays editable at all times (spec: editable during a save).
 *
 * Visual styling lives in `milkdown.css` (imported here) and is documented for
 * Slice C in `markdownClassNames`/the doc comment in PadEditor — Plex Mono
 * headings, h2 hairline rule, accent ordered-list numbers, sunken code blocks,
 * 2px accent blockquote border, etc. — all via design tokens, no hard-coded
 * colors.
 *
 * `setMarkdown` (imperative) lets the parent replace the whole document when a
 * conflict is resolved ("Reload theirs" loads the server version). It is
 * exposed through a ref so the parent can call it without re-mounting.
 */
import { Editor, defaultValueCtx, rootCtx } from "@milkdown/core";
import { listener, listenerCtx } from "@milkdown/plugin-listener";
import { commonmark } from "@milkdown/preset-commonmark";
import { gfm } from "@milkdown/preset-gfm";
import { Milkdown, MilkdownProvider, useEditor } from "@milkdown/react";
import { replaceAll } from "@milkdown/utils";
import { forwardRef, useCallback, useImperativeHandle, useRef, type ReactNode } from "react";
import "./milkdown.css";

export interface MilkdownEditorHandle {
  /** Replace the whole document with new markdown (used on conflict resolve). */
  setMarkdown: (markdown: string) => void;
}

export interface MilkdownEditorProps {
  /** Initial markdown body. Only read on first mount. */
  defaultValue: string;
  /** Fired with the serialized markdown whenever the document changes. */
  onChange: (markdown: string) => void;
}

/**
 * MilkdownEditor wraps the inner editor in {@link MilkdownProvider} so callers
 * (PadEditor) don't need to manage the Milkdown React context themselves.
 */
export const MilkdownEditor = forwardRef<MilkdownEditorHandle, MilkdownEditorProps>(
  function MilkdownEditor(props, ref): ReactNode {
    return (
      <MilkdownProvider>
        <MilkdownEditorInner {...props} ref={ref} />
      </MilkdownProvider>
    );
  },
);

const MilkdownEditorInner = forwardRef<MilkdownEditorHandle, MilkdownEditorProps>(
  function MilkdownEditorInner({ defaultValue, onChange }, ref): ReactNode {
    // Hold the latest onChange so the editor factory (created once) always
    // calls the current callback without re-instantiating Milkdown.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const { get } = useEditor((root) =>
      Editor.make()
        .config((ctx) => {
          ctx.set(rootCtx, root);
          ctx.set(defaultValueCtx, defaultValue);
          ctx.get(listenerCtx).markdownUpdated((_ctx, markdown) => {
            onChangeRef.current(markdown);
          });
        })
        // `commonmark` MUST be used before `gfm`: it provides the base schema
        // (doc/paragraph/heading/etc.). `gfm` only extends it (tables, task
        // lists, strikethrough). Without commonmark the ProseMirror schema has
        // no top `doc` node and the editor fails to mount.
        .use(listener)
        .use(commonmark)
        .use(gfm),
    );

    const setMarkdown = useCallback(
      (markdown: string) => {
        const editor = get();
        if (!editor) return;
        // `replaceAll` re-parses the markdown and swaps the whole document
        // (used by "Reload theirs" to load the server version).
        editor.action(replaceAll(markdown));
      },
      [get],
    );

    useImperativeHandle(ref, () => ({ setMarkdown }), [setMarkdown]);

    return (
      <div className="milkdown-host" data-testid="milkdown-host">
        <Milkdown />
      </div>
    );
  },
);
